import React, { useState } from 'react'
import { AddVendorToBlacklistInput, VendorBlacklistEntry, Chef } from '@types'
import { AutocompleteInput } from '@containers/common/form'
import { MdOutlineEdit } from 'react-icons/md'
import moment from 'moment'
import Loader from '@components/common/Loader'
import { disabledClasses } from '@components/groupOrders/UniversalMenuPreview/utils'

type BaseProps = {
  list: VendorBlacklistEntry[]
  editable?: boolean
  close: () => void
}

// Editable mode
type EditableProps = BaseProps & {
  editable?: true
  addVendorToBlacklist: (
    input: AddVendorToBlacklistInput,
  ) => Promise<VendorBlacklistEntry>
  updateVendorBlacklistEntry: (input: {
    vendorId: string
    active: boolean
    reason: string
  }) => Promise<VendorBlacklistEntry>
  onChange: (updatedList: VendorBlacklistEntry[]) => void
  loadChefs: (filters: { search?: string; status: 'active' }) => Promise<void>
}

// Read-only mode
type ReadonlyProps = BaseProps & {
  editable: false
  onChange?: never
  addVendorToBlacklist?: never
  updateVendorBlacklistEntry?: never
  loadChefs?: never
}

type VendorBlacklistProps = EditableProps | ReadonlyProps

const VendorBlacklist: React.FC<VendorBlacklistProps> = ({
  editable,
  list,
  addVendorToBlacklist,
  updateVendorBlacklistEntry,
  onChange,
  loadChefs,
  close,
}) => {
  const [currentList, setCurrentList] = useState(list)
  const [newSelected, toggleNewSelected] = useState(editable)
  const [selectedVendor, setSelectedVendor] = useState<Chef | undefined>()
  const [blacklistReason, setBlacklistReason] = useState('')
  const [edittingListMap, setEdittingListMap] = useState<
    Record<string, string>
  >({})
  const [isLoading, setIsLoading] = useState(false)

  const excludedChefIds = currentList.map((e) => e.vendorId)

  const handleAddToList = async () => {
    if (!editable || !selectedVendor || !blacklistReason) {
      return
    }
    setIsLoading(true)
    const vendorEntry = await addVendorToBlacklist({
      vendorId: selectedVendor?.id,
      vendorName: selectedVendor?.name,
      reason: blacklistReason,
    })
    if (vendorEntry) {
      const nextList = [...currentList, vendorEntry]
      setCurrentList(nextList)
      onChange(nextList)
      setSelectedVendor(undefined)
      setBlacklistReason('')
    }
    setIsLoading(false)
  }

  const onCancelEditAudit = (entry: VendorBlacklistEntry) => {
    if (!editable) {
      return
    }
    const nextMap = { ...edittingListMap }
    delete nextMap[entry.vendorId]

    setEdittingListMap(nextMap)
  }

  const onUpdateBlacklistEntry = async (entry: VendorBlacklistEntry) => {
    if (!editable) {
      return
    }
    setIsLoading(true)
    const nextReason = edittingListMap[entry.vendorId] || ''
    const updatedEntry = await updateVendorBlacklistEntry({
      vendorId: entry.vendorId,
      reason: nextReason,
      active: !entry.active,
    })
    if (updatedEntry) {
      const nextList = [...currentList].map((e) =>
        e.vendorId == updatedEntry.vendorId ? updatedEntry : e,
      )
      setCurrentList(nextList)
      onChange(nextList)
      onCancelEditAudit(entry)
    }
    setIsLoading(false)
  }

  const renderPrevAuditChanges = (entry: VendorBlacklistEntry) => {
    return entry.audits.map((audit, i) => {
      return (
        <div
          className="flex flex-row w-full items-center justify-between"
          key={i}
        >
          <p>
            {audit.active ? 'Activated' : 'Deactivated'} on{' '}
            {moment(audit.updatedAt).format('MM/DD/YY H:mm a')} by{' '}
            {audit.updatedBy}
            {': '}
            <span className="font-bold">{audit.reason}</span>
          </p>
        </div>
      )
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-40 w-full" style={{ height: '650px ' }}>
        <Loader />
      </div>
    )
  }

  return (
    <div className="min-h-40 w-full" style={{ height: '650px ' }}>
      <div className="flex flex-row items-center justify-around space-x-2 pb-2">
        <button
          className={disabledClasses(
            !editable,
            `${
              newSelected
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 border border-solid border-gray-300'
            } font-semibold ${
              editable ? 'hover:cursor-pointer' : ''
            } hover:bg-green-600 hover:text-white w-1/2 rounded-md h-10`,
            'opacity-40 cursor-not-allowed',
          )}
          onClick={() => toggleNewSelected(true)}
          disabled={!editable}
        >
          Add New +
        </button>
        <button
          className={`${
            !newSelected
              ? 'bg-green-600 text-white'
              : 'bg-white text-gray-700 border border-solid border-gray-300'
          } font-semibold hover:cursor-pointer hover:bg-green-600 hover:text-white w-1/2 rounded-md h-10`}
          onClick={() => toggleNewSelected(false)}
        >
          Manage Existing
        </button>
      </div>

      {newSelected ? (
        <div className="h-400 min-h-45 flex flex-col items-start justify-start w-full border-solid border-gray-600 bg-gray-300 rounded-md pl-3">
          <div className="w-full border border-gray-200 border-solid py-3 rounded-tr-md rounded-tl-md">
            {loadChefs && editable ? (
              <AutocompleteInput
                label="Add Vendor To Blacklist"
                width="45%"
                displayAttribute="name"
                loaderFunction={(search) =>
                  loadChefs({
                    ...search,
                    excludingChefIds: excludedChefIds,
                    status: 'active',
                  })
                }
                onSelect={(vendor) => setSelectedVendor(vendor)}
              />
            ) : (
              <div className="px-4 py-2 text-sm text-gray-700">Read-only</div>
            )}
          </div>
          {selectedVendor && editable && (
            <div className="w-full flex flex-col items-start justify-center border-gray-400 border-solid py-2">
              <label className="text-gray-label font-bold uppercase text-13px">
                Blacklist Reason
              </label>
              <input
                type="text"
                placeholder="Enter reason for blacklisting this vendor..."
                value={blacklistReason}
                onChange={(e) => setBlacklistReason(e.target.value)}
                className="w-1/2 h-10 rounded-md"
              />
            </div>
          )}
          <div className="flex flex-col items-center w-full py-5">
            <button
              className={disabledClasses(
                !editable || !blacklistReason,
                'w-3/4 rounded-md bg-green-600 text-white font-bold h-10',
                'opacity-25 cursor-not-allowed',
              )}
              disabled={!editable || !blacklistReason}
              onClick={() => handleAddToList()}
            >
              Add to Blacklist
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gray-200 rounded-lg h-500">
          <label className="text-gray-label uppercase font-bold pl-6 py-3">
            Existing Blacklisted Vendors
          </label>
          <div className="h-400 rounded-lg border border-solid border-gray-400 mx-6 mb-5">
            <div className="h-full w-full overflow-x-auto overflow-y-auto">
              <table className="w-full h-full">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-400">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-400">
                      Latest Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-400">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentList.map((blacklistEntry) => {
                    const isEditting =
                      edittingListMap[blacklistEntry.vendorId] !== undefined

                    return (
                      <tr
                        key={blacklistEntry.vendorId}
                        className="border-t-2 border-gray-400 border-solid"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {blacklistEntry.vendorName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {isEditting ? (
                            <div className="flex flex-col justify-evenly'">
                              {renderPrevAuditChanges(blacklistEntry)}
                              <input
                                type="text"
                                onChange={(e) =>
                                  setEdittingListMap({
                                    ...edittingListMap,
                                    [blacklistEntry.vendorId]: e.target.value,
                                  })
                                }
                                className={disabledClasses(
                                  !editable,
                                  'px-2 py-2 rounded-md w-full bg-white border border-solid border-gray-400',
                                  'opacity-50 cursor-not-allowed',
                                )}
                              />
                              <button
                                className={disabledClasses(
                                  !editable,
                                  'w-32 mt-3 px-2 py-2 rounded-lg font-semibold bg-white border-solid border-gray-300',
                                  'opacity-50 cursor-not-allowed',
                                )}
                                onClick={() =>
                                  onCancelEditAudit(blacklistEntry)
                                }
                                disabled={!editable}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            blacklistEntry.reason
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {isEditting ? (
                            <div>
                              <button
                                onClick={() =>
                                  onUpdateBlacklistEntry(blacklistEntry)
                                }
                                disabled={
                                  !editable ||
                                  !edittingListMap[blacklistEntry.vendorId]
                                }
                                className={disabledClasses(
                                  !editable,
                                  `${
                                    !edittingListMap[blacklistEntry.vendorId]
                                      ? 'bg-red-700'
                                      : 'bg-green-600'
                                  } text-white px-2 py-2 rounded-full font-bold`,
                                  'opacity-50 cursor-not-allowed',
                                )}
                              >
                                {blacklistEntry.active
                                  ? 'Deactivate'
                                  : 'Activate'}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                className={disabledClasses(
                                  !editable,
                                  `flex items-center justify-evenly px-2 py-2 rounded-full text-white font-bold ${
                                    blacklistEntry.active
                                      ? 'bg-red-700'
                                      : 'bg-green-600'
                                  } hover:cursor-pointer`,
                                  'opacity-50 cursor-not-allowed',
                                )}
                                disabled={!editable}
                                onClick={() =>
                                  setEdittingListMap({
                                    ...edittingListMap,
                                    [blacklistEntry.vendorId]: '',
                                  })
                                }
                              >
                                {blacklistEntry.active
                                  ? 'Blacklisted'
                                  : 'Inactive'}
                                <MdOutlineEdit />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col w-full items-center py-10">
        <button
          className="bg-green-600 rounded-md font-bold text-white h-10 w-1/3"
          onClick={close}
        >
          Done
        </button>
      </div>
    </div>
  )
}

export default VendorBlacklist

interface ClosedBlacklistedBtnProps {
  label: string
  listCount: number
  toggleOpen: (open: boolean) => void
}

export const ClosedBlacklistedBtn: React.FC<ClosedBlacklistedBtnProps> = ({
  label,
  listCount,
  toggleOpen,
}) => {
  return (
    <button
      className="group flex flex-row hover:bg-green-600 items-center justify-between border border-solid border-gray-500 rounded-md w-full py-2 px-4 space-x-3 font-bold text-md text-gray-800 hover:text-white"
      onClick={() => toggleOpen(true)}
    >
      <span className="pr-2">{label}</span>
      <div className="h-5 w-5 grid place-items-center rounded-md bg-red-600 text-white text-md font-bold">
        {listCount}
      </div>
    </button>
  )
}
